import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import AdminLayout from "../components/AdminLayout";
import VoucherImport from "../components/VoucherImport";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  createTheme,
  ThemeProvider,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  FileUpload as ImportIcon,
  LocalOffer as VoucherIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

// Styled Components
const VouchersContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const VouchersHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const ActionsContainer = styled('div')`
  display: flex;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);
  flex-wrap: wrap;
`;

const DataGridContainer = styled('div')`
  height: 600px;
  margin-top: var(--space-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiDataGrid-root {
    border: none;
    background: transparent;
    color: var(--text-primary);
  }

  .MuiDataGrid-columnHeaders {
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border);
  }

  .MuiDataGrid-columnHeader {
    color: var(--text-primary);
    font-weight: 600;
  }

  .MuiDataGrid-row {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .MuiDataGrid-cell {
    color: var(--text-secondary);
    border-bottom: none;
  }

  .MuiDataGrid-footerContainer {
    background: var(--bg-tertiary);
    border-top: 1px solid var(--border);
    color: var(--text-secondary);
  }
`;

// Dark theme for Material-UI components
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    secondary: {
      main: '#06b6d4',
    },
    error: {
      main: '#ef4444',
    },
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    background: {
      default: '#0a0f0a',
      paper: '#2d342d',
    },
    text: {
      primary: '#f0f9f0',
      secondary: '#c8d8c8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          },
        },
        outlined: {
          borderColor: '#10b981',
          color: '#10b981',
          '&:hover': {
            borderColor: '#34d399',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2d342d',
          backgroundImage: 'none',
          border: '1px solid #374151',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
  },
});

function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "fixed",
    value: "",
    min_spend: "",
    expiry_date: "",
    status: "active",
  });

  // ✅ Fetch vouchers from Firestore
  const fetchVouchers = async () => {
    const querySnapshot = await getDocs(collection(db, "vouchers"));
    const data = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setVouchers(data);
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  // ✅ Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Save or Update Voucher
  const handleSave = async () => {
    if (editId) {
      const voucherRef = doc(db, "vouchers", editId);
      await updateDoc(voucherRef, {
        ...formData,
        value: parseFloat(formData.value),
        min_spend: parseFloat(formData.min_spend),
      });
    } else {
      await addDoc(collection(db, "vouchers"), {
        ...formData,
        value: parseFloat(formData.value),
        min_spend: parseFloat(formData.min_spend),
      });
    }
    fetchVouchers();
    setOpen(false);
    resetForm();
  };

  // ✅ Edit Voucher
  const handleEdit = (voucher) => {
    setFormData({
      code: voucher.code,
      description: voucher.description,
      discount_type: voucher.discount_type,
      value: voucher.value,
      min_spend: voucher.min_spend,
      expiry_date: voucher.expiry_date,
      status: voucher.status,
    });
    setEditId(voucher.id);
    setOpen(true);
  };

  // ✅ Delete Voucher
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "vouchers", id));
    fetchVouchers();
  };

  // ✅ Reset form after save
  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discount_type: "fixed",
      value: "",
      min_spend: "",
      expiry_date: "",
      status: "active",
    });
    setEditId(null);
  };

  const columns = [
    { field: "code", headerName: "Code", width: 120, resizable: true },
    { field: "description", headerName: "Description", width: 250, resizable: true },
    {
      field: "discount_type",
      headerName: "Type",
      width: 120,
      resizable: true,
      renderCell: (params) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '16px',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: params.value === 'fixed' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(6, 182, 212, 0.1)',
          color: params.value === 'fixed' ? '#10b981' : '#06b6d4'
        }}>
          {params.value === 'fixed' ? 'Fixed' : 'Percent'}
        </span>
      )
    },
    { field: "value", headerName: "Value", width: 100, resizable: true },
    { field: "min_spend", headerName: "Min Spend", width: 120, resizable: true },
    { field: "expiry_date", headerName: "Expiry Date", width: 120, resizable: true },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      resizable: true,
      renderCell: (params) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '16px',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: params.value === 'active' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          color: params.value === 'active' ? '#22c55e' : '#ef4444'
        }}>
          {params.value === 'active' ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      resizable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const handleImportComplete = () => {
    fetchVouchers();
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <VouchersContainer>
          <VouchersHeader>
            <PageTitle>
              <VoucherIcon />
              Voucher Management
            </PageTitle>
          </VouchersHeader>

          <ActionsContainer>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Voucher
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ImportIcon />}
              onClick={() => setImportOpen(true)}
            >
              Import Vouchers
            </Button>
          </ActionsContainer>

          {/* Voucher Table */}
          <DataGridContainer>
            <DataGrid
              rows={vouchers}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              getRowId={(row) => row.id}
              disableColumnMenu
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'var(--surface-hover)',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </DataGridContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editId ? "Edit Voucher" : "Add Voucher"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Code"
            name="code"
            fullWidth
            value={formData.code}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            value={formData.description}
            onChange={handleChange}
            margin="dense"
          />
          <FormControl fullWidth style={{ marginTop: "15px" }}>
            <InputLabel>Discount Type</InputLabel>
            <Select
              name="discount_type"
              value={formData.discount_type}
              onChange={handleChange}
            >
              <MenuItem value="fixed">Fixed Amount (RM)</MenuItem>
              <MenuItem value="percent">Percentage (%)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Value"
            name="value"
            fullWidth
            type="number"
            value={formData.value}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Minimum Spend"
            name="min_spend"
            fullWidth
            type="number"
            value={formData.min_spend}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Expiry Date"
            name="expiry_date"
            type="date"
            fullWidth
            value={formData.expiry_date}
            onChange={handleChange}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth style={{ marginTop: "15px" }}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <VoucherImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={handleImportComplete}
      />
        </VouchersContainer>
      </AdminLayout>
    </ThemeProvider>
  );
}

export default Vouchers;
